import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from 'react-dom';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_CONFIG, loadAppConfig } from '../../config/apiConfig';
import './index.css';

// Portal 组件用于将下拉菜单渲染到 body，并计算正确位置
function DropdownPortal({ children, isOpen, filterRef }) {
    const [position, setPosition] = useState({ top: 0, left: 0 });
    
    useEffect(() => {
        if (isOpen && filterRef?.current) {
            const updatePosition = () => {
                const rect = filterRef.current.getBoundingClientRect();
                
                // 计算下拉菜单的合适位置
                let left = rect.left;
                let top = rect.bottom;
                
                // 确保下拉菜单不会超出屏幕右边界
                const dropdownWidth = 180; // 调整宽度以适应筛选框大小
                const screenWidth = window.innerWidth;
                if (left + dropdownWidth > screenWidth - 10) {
                    left = screenWidth - dropdownWidth - 10;
                }
                
                // 确保下拉菜单不会超出屏幕底部
                // 移除了固定高度限制，让内容自然展开
                const screenHeight = window.innerHeight;
                // 简单检查底部空间，如果不足则向上显示
                if (top + 100 > screenHeight - 10) { // 100px为预估的最小高度
                    top = rect.top - 100 - 5; // 向上显示
                }
                
                setPosition({
                    top,
                    left
                });
            };
            updatePosition();
            
            // 监听滚动和窗口大小变化
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
            
            return () => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [isOpen, filterRef]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div
            className="filter-dropdown"
            style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                zIndex: 99
            }}
        >
            {children}
        </div>,
        document.body
    );
}

/**
 * 渲染 12 小时段的时间轴（包含顶部时间标签 + 预约时段 + 状态时段）
 */
function renderTimeAxisSegment(
    vehicle,
    vehicleTimes,
    selectedDate,
    segmentStart,
    segmentEnd,
    status,
    formatTime,
    hoveredReservationId,
    popupPosition,
    setPopupPosition,
    setHoveredReservationId,
    setHoveredReservationData,
    setShowReservationDetail
) {
    const segmentDuration = segmentEnd - segmentStart;

    // 生成段内的小时刻度
    const hourMarks = [];
    for (let h = segmentStart; h <= segmentEnd; h++) {
        hourMarks.push(h);
    }

    // 计算 reservation 时段（橙色条）
    const dateParts = selectedDate.split('-');
    const selectedDateOnly = new Date(dateParts[0], parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));

    const reservationSlotNodes = vehicleTimes.map((timeSlot, index) => {
        const startDateTime = new Date(timeSlot.startTime);
        const endDateTime = new Date(timeSlot.endTime);
        const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
        const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

        let startHour = 0, endHour = 0;
        if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
            const sParts = timeSlot.startTime.split(' ')[1].split(':');
            const eParts = timeSlot.endTime.split(' ')[1].split(':');
            startHour = parseInt(sParts[0], 10) + parseInt(sParts[1], 10) / 60;
            endHour = parseInt(eParts[0], 10) + parseInt(eParts[1], 10) / 60;
        } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
            const sParts = timeSlot.startTime.split(' ')[1].split(':');
            startHour = parseInt(sParts[0], 10) + parseInt(sParts[1], 10) / 60;
            endHour = 24;
        } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
            const eParts = timeSlot.endTime.split(' ')[1].split(':');
            startHour = 0;
            endHour = parseInt(eParts[0], 10) + parseInt(eParts[1], 10) / 60;
        } else if (selectedDateOnly > startDateOnly && selectedDateOnly < endDateOnly) {
            startHour = 0;
            endHour = 24;
        } else {
            return null;
        }

        // 裁剪到当前 12 小时段
        const clippedStart = Math.max(startHour, segmentStart);
        const clippedEnd = Math.min(endHour, segmentEnd);
        if (clippedStart >= clippedEnd) return null;

        const left = ((clippedStart - segmentStart) / segmentDuration) * 100;
        const width = ((clippedEnd - clippedStart) / segmentDuration) * 100;

        const reservationId = `${vehicle.vehicleId}-res-${segmentStart}-${segmentEnd}-${clippedStart}-${clippedEnd}-${index}-${vehicleTimes.length}`;

        // ===== 移动端滑动优化：区分"点按查看详情"与"滑动翻页" =====
        // 手指滑动超过 10px 视为翻页手势：不弹详情、不拦截，页面正常上下滚动；
        // 点按（位移 < 10px）才弹出预约详情，3.5 秒后自动收起，点同一段可再次收起
        let gestureTap = false;
        let gestureStartX = 0;
        let gestureStartY = 0;
        let popupHideTimer = null;

        const hideReservationPopup = () => {
            if (popupHideTimer) { clearTimeout(popupHideTimer); popupHideTimer = null; }
            setHoveredReservationId(null);
            setHoveredReservationData(null);
            setShowReservationDetail(false);
        };

        const showReservationPopup = (currentTarget) => {
            const rect = currentTarget.getBoundingClientRect();
            setPopupPosition({
                top: rect.top - 120,
                left: rect.left + (rect.width / 2) - 150
            });
            setHoveredReservationId(reservationId);
            setHoveredReservationData(timeSlot);
            setShowReservationDetail(true);
            if (popupHideTimer) clearTimeout(popupHideTimer);
            popupHideTimer = setTimeout(hideReservationPopup, 3500);
        };

        return (
            <div
                key={reservationId}
                className="time-slot"
                style={{
                    left: `${left}%`,
                    width: width >= 100 ? '100%' : `max(${width}%, 40px)`,
                    backgroundColor: '#faad14',
                    position: 'absolute',
                    top: 0,
                    height: '100%',
                    zIndex: 10,
                    opacity: 0.9,
                    cursor: 'pointer',
                    padding: 0,
                    margin: 0,
                    boxSizing: 'border-box'
                }}
                title={`${formatTime(timeSlot.startTime)} - ${formatTime(timeSlot.endTime)}`}
                onMouseEnter={(e) => {
                    showReservationPopup(e.currentTarget);
                }}
                onMouseLeave={(e) => {
                    const modal = document.querySelector('.reservation-detail-modal');
                    const isOverModal = modal && modal.contains(e.relatedTarget);
                    const timeSlots = document.querySelectorAll('.time-slot');
                    const isOverOtherTimeSlot = Array.from(timeSlots).some(slot =>
                        slot !== e.currentTarget && slot.contains(e.relatedTarget)
                    );
                    if (!isOverModal && !isOverOtherTimeSlot) {
                        hideReservationPopup();
                    }
                }}
                // 阻止点按橙色段时冒泡到车辆卡片（误触发选车跳转），查看详情和选车分开
                onClick={(e) => e.stopPropagation()}
                onTouchStart={(e) => {
                    const t = e.touches[0];
                    gestureStartX = t.clientX;
                    gestureStartY = t.clientY;
                    gestureTap = true;
                }}
                onTouchMove={(e) => {
                    const t = e.touches[0];
                    if (Math.abs(t.clientX - gestureStartX) > 10 || Math.abs(t.clientY - gestureStartY) > 10) {
                        gestureTap = false;
                        hideReservationPopup();
                    }
                }}
                onTouchEnd={(e) => {
                    if (!gestureTap) return;
                    // 点按：切换显示预约详情（已显示则收起）
                    if (hoveredReservationId === reservationId) {
                        hideReservationPopup();
                        return;
                    }
                    showReservationPopup(e.currentTarget);
                }}
                onTouchCancel={() => {
                    gestureTap = false;
                    hideReservationPopup();
                }}
            ></div>
        );
    }).filter(Boolean);

    // 计算状态时段（绿/黄/灰）
    const now = new Date();
    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let currentTime = -1;
    if (selectedDateOnly.getTime() === currentDate.getTime()) {
        currentTime = now.getHours() + now.getMinutes() / 60;
    }

    const reservationSlots = [];
    for (const time of vehicleTimes) {
        const startDateTime = new Date(time.startTime);
        const endDateTime = new Date(time.endTime);
        const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
        const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

        if (selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly) {
            if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
                const sParts = time.startTime.split(' ')[1].split(':');
                const eParts = time.endTime.split(' ')[1].split(':');
                const sH = parseInt(sParts[0], 10) + parseInt(sParts[1], 10) / 60;
                const eH = parseInt(eParts[0], 10) + parseInt(eParts[1], 10) / 60;
                reservationSlots.push({ start: sH, end: eH });
            } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
                const sParts = time.startTime.split(' ')[1].split(':');
                const sH = parseInt(sParts[0], 10) + parseInt(sParts[1], 10) / 60;
                reservationSlots.push({ start: sH, end: 24 });
            } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
                const eParts = time.endTime.split(' ')[1].split(':');
                const eH = parseInt(eParts[0], 10) + parseInt(eParts[1], 10) / 60;
                reservationSlots.push({ start: 0, end: eH });
            } else {
                reservationSlots.push({ start: 0, end: 24 });
            }
        }
    }

    const mergedSlots = [];
    let currentSlot = null;
    const sortedSlots = [...reservationSlots].sort((a, b) => a.start - b.start);
    for (const slot of sortedSlots) {
        if (!currentSlot) {
            currentSlot = { ...slot };
        } else {
            if (slot.start <= currentSlot.end) {
                currentSlot.end = Math.max(currentSlot.end, slot.end);
            } else {
                mergedSlots.push(currentSlot);
                currentSlot = { ...slot };
            }
        }
    }
    if (currentSlot) mergedSlots.push(currentSlot);

    const finalSlots = [];
    let lastEnd = 0;
    for (const reservedSlot of mergedSlots) {
        // 把 lastEnd 到 min(currentTime, 预约开始) 标为 unavailable
        if (currentTime >= 0 && lastEnd < currentTime) {
            const unavailableEnd = Math.min(reservedSlot.start, currentTime);
            if (unavailableEnd > lastEnd) {
                finalSlots.push({ start: lastEnd, end: unavailableEnd, status: 'unavailable' });
            }
            lastEnd = Math.max(lastEnd, unavailableEnd);
        }
        // lastEnd 到预约开始之间有空 → available
        if (reservedSlot.start > lastEnd) {
            finalSlots.push({ start: lastEnd, end: reservedSlot.start, status: 'available' });
            lastEnd = reservedSlot.start;
        }
        // 预约区间：在当前时间之前的部分 → unavailable，之后的部分 → reserved
        const reservedPastEnd = Math.min(reservedSlot.end, currentTime >= 0 ? currentTime : lastEnd);
        if (lastEnd < reservedPastEnd) {
            finalSlots.push({ start: lastEnd, end: reservedPastEnd, status: 'unavailable' });
            lastEnd = reservedPastEnd;
        }
        if (lastEnd < reservedSlot.end) {
            finalSlots.push({ start: lastEnd, end: reservedSlot.end, status: 'reserved' });
            lastEnd = reservedSlot.end;
        }
    }
    // 循环后，lastEnd 到 24 之间：当前时间之前的 grey，之后的 green
    if (currentTime >= 0 && lastEnd < currentTime) {
        finalSlots.push({ start: lastEnd, end: currentTime, status: 'unavailable' });
        lastEnd = currentTime;
    }
    if (lastEnd < 24) {
        finalSlots.push({ start: lastEnd, end: 24, status: 'available' });
    }
    if (mergedSlots.length === 0) {
        if (currentTime >= 0) {
            finalSlots.push({ start: 0, end: currentTime, status: 'unavailable' });
            finalSlots.push({ start: currentTime, end: 24, status: 'available' });
        } else {
            finalSlots.push({ start: 0, end: 24, status: 'available' });
        }
    }

    const validSlots = finalSlots.filter(slot => slot.start < slot.end);
    const statusSlotNodes = validSlots
        .map((slot, idx) => {
            const clippedStart = Math.max(slot.start, segmentStart);
            const clippedEnd = Math.min(slot.end, segmentEnd);
            if (clippedStart >= clippedEnd) return null;

            const left = ((clippedStart - segmentStart) / segmentDuration) * 100;
            const width = ((clippedEnd - clippedStart) / segmentDuration) * 100;

            let backgroundColor;
            switch (slot.status) {
                case 'available':
                    backgroundColor = 'rgba(82, 196, 26, 0.6)';
                    break;
                case 'reserved':
                    backgroundColor = 'rgba(250, 173, 20, 0.8)';
                    break;
                case 'unavailable':
                    backgroundColor = 'rgba(191, 191, 191, 0.8)';
                    break;
                default:
                    backgroundColor = 'rgba(204, 204, 204, 0.3)';
            }

            return (
                <div
                    key={`status-${vehicle.vehicleId}-${segmentStart}-${segmentEnd}-${clippedStart}-${clippedEnd}-${slot.status}-${idx}`}
                    className={`time-slot-status ${slot.status}`}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: `${left}%`,
                        width: width >= 100 ? '100%' : `max(${width}%, 2px)`,
                        bottom: 0,
                        backgroundColor: backgroundColor,
                        pointerEvents: 'none',
                        borderRadius: '4px',
                        padding: 0,
                        margin: 0,
                        boxSizing: 'border-box'
                    }}
                ></div>
            );
        })
        .filter(Boolean);

    return (
        <div className="time-axis-segment">
            <div className="time-label">
                {hourMarks.map((h, i) => (
                    <span key={i}>{h}</span>
                ))}
            </div>
            <div
                className={`time-axis ${status === 'unavailable' ? 'unavailable' : ''}`}
                style={{ backgroundColor: 'transparent' }}
            >
                {reservationSlotNodes}
                {statusSlotNodes}
            </div>
        </div>
    );
}

export default function VehicleSelect() {
    const navigate = useNavigate();
    
    // 状态管理
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [vehicleUsageTimes, setVehicleUsageTimes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [vehicleGroups, setVehicleGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [availableSeats, setAvailableSeats] = useState([{ seatName: '全部' }]);
    const [selectedSeats, setSelectedSeats] = useState('全部');
    // 预约详情弹窗
    const [showReservationDetail, setShowReservationDetail] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState(null);
    // 用于跟踪当前悬停/触摸的预约时间段
    const [hoveredReservationId, setHoveredReservationId] = useState(null);
    const [hoveredReservationData, setHoveredReservationData] = useState(null);
    // 用于跟踪弹窗位置
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
    // 下拉框显示状态管理
    const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);
    const [isSeatsDropdownOpen, setIsSeatsDropdownOpen] = useState(false);
    // 城市归属地筛选
    const [cityList, setCityList] = useState([]);
    const [selectedCity, setSelectedCity] = useState('');
    const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
    // 状态筛选（all / available / partial / reserved）
    const [statusFilter, setStatusFilter] = useState('all');
    // 使用ref追踪筛选容器和筛选框
    const filterContainerRef = useRef(null);
    const groupFilterRef = useRef(null);
    const seatsFilterRef = useRef(null);
    const cityFilterRef = useRef(null);
    
    // 生成未来7天的日期
    function generateNext7Days() {
        const days = [];
        const today = new Date();

        for (let i = 0; i < 15; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const weekDay = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][date.getDay()];

            days.push({
                date: `${year}-${month}-${day}`,
                day: day,
                weekDay: weekDay
            });
        }

        return days;
    }
    
    // 格式化时间为24小时制
    function formatTime(time) {
        return time.substring(5, 16);
    }
    
    // 计算时间槽的位置和宽度，统一使用24小时（支持分钟）
    const calculateTimeSlotPosition = (startTime, endTime) => {
        // 提取开始和结束日期
        const startDate = startTime.split(' ')[0];
        const endDate = endTime.split(' ')[0];
        
        // 检测是否跨天
        const isCrossDay = startDate !== endDate;
        
        // 直接从字符串中提取日期和时间，确保使用正确的日期和小时
        const startParts = startTime.split(' ')[1].split(':');
        const endParts = endTime.split(' ')[1].split(':');
        
        // 提取开始和结束时间（包含分钟，转换为小时小数）
        const startHour = parseInt(startParts[0], 10) + parseInt(startParts[1], 10) / 60;
        let endHour = parseInt(endParts[0], 10) + parseInt(endParts[1], 10) / 60;
        
        // 如果跨天，结束时间设为24点
        if (isCrossDay) {
            endHour = 24;
        }
        
        // 统一使用24小时
        const maxHours = 24;
        
        // 确保位置在0-24小时范围内
        const normalizedStart = Math.max(0, Math.min(maxHours, startHour));
        const normalizedEnd = Math.max(0, Math.min(maxHours, endHour));
        
        // 计算开始位置：startHour / 24 * 100%
        const left = (normalizedStart / maxHours) * 100;
        
        // 计算宽度，确保只显示到结束时间点
        const hoursDuration = normalizedEnd - normalizedStart;
        const width = (hoursDuration / maxHours) * 100;
        
        return { left: `${left}%`, width: `${width}%` };
    };
    
    // 获取车辆列表
    const fetchVehicles = async () => {
        try {
            setLoading(true);
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/getVehicleDeviceListByCompanyId`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.data && response.data.code === 200) {
                setVehicles(response.data.data || []);
                // 提取城市列表（去重）
                const cities = [...new Set((response.data.data || []).map(v => v.cityName).filter(Boolean))]
                setCityList(cities)
            } else {
                setVehicles([]);
            }
        } catch (error) {
            console.error('获取车辆列表失败:', error);
            setVehicles([]);
        } finally {
            setLoading(false);
        }
    };
    
    // 获取车辆已预约时间
    const fetchVehicleAvailableTime = async (plateNumber = '') => {
        try {
            const params = {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                startTime: `${selectedDate} 00:00:00`,
                endTime: `${selectedDate} 23:59:59`
            };
            
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/getVehicleAvailableTime`, params, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.data && response.data.code === 200) {
                let usageTimes = response.data.data || [];
                
                // 保留所有在选定日期范围内有效的预约时间
                const filteredUsageTimes = usageTimes.filter(item => {
                    // 检查选定日期是否在预约时间范围内（包括开始日期、中间日期和结束日期）
                    const startDateTime = new Date(item.startTime);
                    const endDateTime = new Date(item.endTime);
                    const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                    const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
                    
                    const dateParts = selectedDate.split('-');
                    const selectedDateOnly = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                    
                    return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
                });
                
                // 如果指定了车牌号，进一步筛选该车辆的预约时间
                if (plateNumber) {
                    // 先找到对应的车辆ID
                    const targetVehicle = vehicles.find(v => v.plateNumber === plateNumber);
                    if (targetVehicle) {
                        usageTimes = filteredUsageTimes.filter(item => {
                            return item.vehicleId === targetVehicle.vehicleId;
                        });
                    } else {
                        usageTimes = [];
                    }
                } else {
                    usageTimes = filteredUsageTimes;
                }
                
                setVehicleUsageTimes(usageTimes);
            } else {
                setVehicleUsageTimes([]);
            }
        } catch (error) {
            console.error('获取车辆已预约时间失败:', error);
            setVehicleUsageTimes([]);
        }
    };
    
    // 获取车辆分组列表
    const fetchVehicleGroups = async () => {
        try {
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/getVehicleGroups`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.data && response.data.code === 200) {
                setVehicleGroups(response.data.data || []);
            }
        } catch (error) {
            console.error('获取车辆分组失败:', error);
        }
    };
    
    // 获取座位数列表
    const fetchCarSeats = async () => {
        try {
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/getCarSeats`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.data && response.data.code === 200) {
                setAvailableSeats([{ seatName: '全部' }, ...(response.data.data || [])]);
            }
        } catch (error) {
            console.error('获取座位数失败:', error);
        }
    };

    // 选择车辆
    const handleVehicleSelect = (vehicle) => {
        // 处理车辆分组信息，确保传递中文名称
        const vehicleWithGroupName = {
            ...vehicle,
            group: typeof vehicle.group === 'number' ? getGroupNameById(vehicle.group) : vehicle.group
        };

        // 计算当前车辆在选定日期的可预约时间信息
        const availableTimeInfo = calculateAvailableTimeInfo(vehicle);

        // 跳转到新的时间选择页面，传递车辆信息、日期和可预约时间作为URL参数
        const vehicleStr = JSON.stringify(vehicleWithGroupName);
        const encodedVehicle = encodeURIComponent(vehicleStr);
        const encodedDate = encodeURIComponent(selectedDate);
        const encodedAvailableTime = availableTimeInfo ? encodeURIComponent(JSON.stringify(availableTimeInfo)) : '';
        navigate(`/time-select?vehicle=${encodedVehicle}&date=${encodedDate}${encodedAvailableTime ? `&availableTime=${encodedAvailableTime}` : ''}`);
    };
    // 计算车辆在选定日期的可预约时间信息
    const calculateAvailableTimeInfo = (vehicle) => {
        const dateParts = selectedDate.split('-');
        const selectedDateOnly = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
        const now = new Date();
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        let currentTime = -1;
        if (selectedDateOnly.getTime() === currentDate.getTime()) {
            currentTime = now.getHours() + (now.getMinutes() / 60);
        }

        const vehicleTimesForSummary = vehicleUsageTimes
            .filter(item => {
                const isVehicleMatch = item.vehicleId && vehicle.vehicleId && item.vehicleId === vehicle.vehicleId;
                const startDateTime = new Date(item.startTime);
                const endDateTime = new Date(item.endTime);
                const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
                const isDateInRange = selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
                return isVehicleMatch && isDateInRange;
            })
            .filter((item, index, self) => {
                const key = `${item.startTime}-${item.endTime}`;
                return self.findIndex(t => `${t.startTime}-${t.endTime}` === key) === index;
            });

        // 计算可预约时间段
        const mergedSlots = [];
        const sortedSlots = [...vehicleTimesForSummary].sort((a, b) => {
            const aStart = new Date(a.startTime);
            const bStart = new Date(b.startTime);
            return aStart - bStart;
        });

        for (const time of sortedSlots) {
            const startDateTime = new Date(time.startTime);
            const endDateTime = new Date(time.endTime);
            const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
            const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

            let startHour, endHour;
            if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
                startHour = startDateTime.getHours() + startDateTime.getMinutes() / 60;
                endHour = endDateTime.getHours() + endDateTime.getMinutes() / 60;
            } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
                startHour = startDateTime.getHours() + startDateTime.getMinutes() / 60;
                endHour = 24;
            } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
                startHour = 0;
                endHour = endDateTime.getHours() + endDateTime.getMinutes() / 60;
            } else {
                startHour = 0;
                endHour = 24;
            }

            if (mergedSlots.length === 0) {
                mergedSlots.push({ start: startHour, end: endHour });
            } else {
                const lastSlot = mergedSlots[mergedSlots.length - 1];
                if (startHour <= lastSlot.end) {
                    lastSlot.end = Math.max(lastSlot.end, endHour);
                } else {
                    mergedSlots.push({ start: startHour, end: endHour });
                }
            }
        }

        // 生成最终的时间段
        const finalSlots = [];
        let lastEnd = 0;

        for (const reservedSlot of mergedSlots) {
            if (currentTime >= 0 && lastEnd < currentTime) {
                const unavailableEnd = Math.min(reservedSlot.start, currentTime);
                if (unavailableEnd > lastEnd) {
                    finalSlots.push({ start: lastEnd, end: unavailableEnd, status: 'unavailable' });
                }
                lastEnd = unavailableEnd;
            }

            if (reservedSlot.start > lastEnd) {
                finalSlots.push({ start: lastEnd, end: reservedSlot.start, status: 'available' });
            }

            const reservedStart = Math.max(reservedSlot.start, currentTime >= 0 ? currentTime : 0);
            if (reservedStart < reservedSlot.end) {
                finalSlots.push({ start: reservedStart, end: reservedSlot.end, status: 'reserved' });
            }

            lastEnd = reservedSlot.end;
        }

        if (lastEnd < 24) {
            if (currentTime >= 0 && lastEnd < currentTime) {
                finalSlots.push({ start: lastEnd, end: currentTime, status: 'unavailable' });
                lastEnd = currentTime;
            }
            if (lastEnd < 24) {
                finalSlots.push({ start: lastEnd, end: 24, status: 'available' });
            }
        }

        if (mergedSlots.length === 0) {
            if (currentTime >= 0) {
                finalSlots.push({ start: 0, end: currentTime, status: 'unavailable' });
                finalSlots.push({ start: currentTime, end: 24, status: 'available' });
            } else {
                finalSlots.push({ start: 0, end: 24, status: 'available' });
            }
        }

        // 格式化时间段显示
        const formatHour = (hour) => {
            const h = Math.floor(hour);
            const m = Math.round((hour - h) * 60);
            return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        };

        // 最小时间间隔阈值（单位：小时），小于这个值的时间段不显示
        const MIN_TIME_THRESHOLD = 5 / 60; // 5分钟

        // 对finalSlots进行去重，避免相同时间段重复显示
        const uniqueSlots = [];
        const seenTimeRanges = new Set();

        finalSlots.forEach(slot => {
            const timeKey = `${slot.start}-${slot.end}`;
            if (!seenTimeRanges.has(timeKey)) {
                seenTimeRanges.add(timeKey);
                uniqueSlots.push(slot);
            }
        });

        // 过滤掉时间间隔过短的可预约时间段
        const availableSlots = uniqueSlots
            .filter(s => s.status === 'available')
            .filter(s => (s.end - s.start) >= MIN_TIME_THRESHOLD);

        if (availableSlots.length === 0) {
            return { text: '🚫 今日无空闲时段', slots: [] };
        }

        if (availableSlots.length === 1) {
            return {
                text: `✅ 可预约：${formatHour(availableSlots[0].start)}-${formatHour(availableSlots[0].end)}`,
                slots: availableSlots.map(slot => ({
                    start: formatHour(slot.start),
                    end: formatHour(slot.end)
                }))
            };
        }

        return {
            text: `✅ 可预约：${availableSlots.map(slot => `${formatHour(slot.start)}-${formatHour(slot.end)}`).join('、')}`,
            slots: availableSlots.map(slot => ({
                start: formatHour(slot.start),
                end: formatHour(slot.end)
            }))
        };
    };


    // 选择日期
    const handleDateSelect = (date) => {
        setSelectedDate(date);
    };
    
    // 选择分组
    const handleGroupSelect = (groupName) => {
        setSelectedGroup(groupName);
    };
    
    // 选择座位数
    const handleSeatsSelect = (seatName) => {
        setSelectedSeats(seatName);
    };
    
    // 初始化数据
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // 先等待租户配置加载完成（loadAppConfig 会从 localStorage 读 tenantKey 并实时拉 Java 后端）
        // 否则 API_CONFIG.apiToken 是 undefined，业务接口会报 "token 参数不能为空"
        (async () => {
            try {
                await loadAppConfig();
            } catch (e) {
                console.warn('[vehicle-select] loadAppConfig failed:', e);
            }
            fetchVehicles();
            fetchVehicleGroups();
            fetchCarSeats();
            // 无论车辆数据是否获取完成，都直接获取当天的预约时间
            fetchVehicleAvailableTime();
        })();
    }, []);
    
    // 日期变化时重新获取数据
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchVehicleAvailableTime();
    }, [selectedDate]);
    
    // 点击外部关闭下拉框
    useEffect(() => {
        const handleClickOutside = (event) => {
            // 检查是否点击了下拉框外部
            if (
                isGroupDropdownOpen && 
                filterContainerRef.current && 
                !filterContainerRef.current.contains(event.target)
            ) {
                setIsGroupDropdownOpen(false);
            }
            
            if (
                isSeatsDropdownOpen && 
                filterContainerRef.current && 
                !filterContainerRef.current.contains(event.target)
            ) {
                setIsSeatsDropdownOpen(false);
            }
        };
        
        // 添加事件监听器
        document.addEventListener('click', handleClickOutside);
        
        // 清理事件监听器
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isGroupDropdownOpen, isSeatsDropdownOpen]);
    
    // 从预约时间数据中提取所有车辆信息
    const extractVehiclesFromUsageTimes = useCallback(() => {
        // 从vehicleUsageTimes中提取唯一的车辆信息
        const uniqueVehicles = [];
        const seenPlateNumbers = new Set();
        
        vehicleUsageTimes.forEach(usage => {
            if (usage.plateNumber && !seenPlateNumbers.has(usage.plateNumber)) {
                seenPlateNumbers.add(usage.plateNumber);
                // 创建车辆对象，使用从usage中提取的数据
                uniqueVehicles.push({
                    plateNumber: usage.plateNumber,
                    vehicleType: usage.modelName || '未知车型',
                    vehicleId: usage.vehicleId,
                    imageUrl: '',
                    seats: 5,
                    group: '',
                    status: 'available'
                });
            }
        });
        
        return uniqueVehicles;
    }, [vehicleUsageTimes]);
    
    // 合并车辆列表和从预约时间中提取的车辆信息
    const allVehicles = useCallback(() => {
        // 如果直接获取的车辆列表有数据，使用它
        if (vehicles.length > 0) {
            return vehicles;
        }
        // 否则，从预约时间数据中提取车辆信息
        return extractVehiclesFromUsageTimes();
    }, [vehicles, extractVehiclesFromUsageTimes]);
    
    // 辅助函数：将分组ID转换为分组名称
    const getGroupNameById = (groupId) => {
        // 查找对应的分组名称
        const group = vehicleGroups.find(g => g.groupId === groupId);
        return group ? group.groupName : '';
    };
    
    // 获取车辆在选定日期的状态
    // ⚠️ 用字符串/数字运算（split），不依赖 new Date() —— iOS 内核无法解析 "yyyy-MM-dd HH:mm:ss" 空格格式
    const getVehicleStatus = (vehicle) => {
        const vehicleTimes = vehicleUsageTimes.filter(item => {
            if (!item.vehicleId || !vehicle.vehicleId || String(item.vehicleId) !== String(vehicle.vehicleId)) {
                return false;
            }

            // 日期范围比较：直接比较 "yyyy-MM-dd" 字符串（字典序即时间序）
            const startDateOnly = String(item.startTime).split(' ')[0];
            const endDateOnly = String(item.endTime).split(' ')[0];

            return selectedDate >= startDateOnly && selectedDate <= endDateOnly;
        });

        if (vehicleTimes.length === 0) {
            return 'available';
        }

        // 当前时间（仅当选定日期是今天）
        const now = new Date();
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        let currentTime = -1;
        if (selectedDate === todayStr) {
            currentTime = now.getHours() + (now.getMinutes() / 60);
        }

        // 合并已预约时间段（用 split 解析时间，避免 Date 解析差异）
        const MIN_TIME_THRESHOLD = 5 / 60; // 5 分钟（与时间摘要一致）
        const mergedSlots = [];
        const sortedSlots = [...vehicleTimes].sort((a, b) => {
            return String(a.startTime).localeCompare(String(b.startTime));
        });

        for (const time of sortedSlots) {
            const startTimeStr = String(time.startTime);
            const endTimeStr = String(time.endTime);
            const startDateStr = startTimeStr.split(' ')[0];
            const endDateStr = endTimeStr.split(' ')[0];

            let startHour, endHour;
            if (selectedDate === startDateStr && selectedDate === endDateStr) {
                const sParts = startTimeStr.split(' ')[1].split(':');
                const eParts = endTimeStr.split(' ')[1].split(':');
                startHour = parseInt(sParts[0], 10) + parseInt(sParts[1], 10) / 60;
                endHour = parseInt(eParts[0], 10) + parseInt(eParts[1], 10) / 60;
            } else if (selectedDate === startDateStr) {
                const sParts = startTimeStr.split(' ')[1].split(':');
                startHour = parseInt(sParts[0], 10) + parseInt(sParts[1], 10) / 60;
                endHour = 24;
            } else if (selectedDate === endDateStr) {
                const eParts = endTimeStr.split(' ')[1].split(':');
                startHour = 0;
                endHour = parseInt(eParts[0], 10) + parseInt(eParts[1], 10) / 60;
            } else {
                startHour = 0;
                endHour = 24;
            }

            if (mergedSlots.length === 0) {
                mergedSlots.push({ start: startHour, end: endHour });
            } else {
                const lastSlot = mergedSlots[mergedSlots.length - 1];
                if (startHour <= lastSlot.end) {
                    lastSlot.end = Math.max(lastSlot.end, endHour);
                } else {
                    mergedSlots.push({ start: startHour, end: endHour });
                }
            }
        }

        // 计算可预约时间段（应用 5 分钟阈值，1 秒级空闲视为不可预约）
        let lastEnd = 0;
        let hasAvailableTime = false;

        for (const reservedSlot of mergedSlots) {
            if (currentTime >= 0 && lastEnd < currentTime) {
                lastEnd = Math.min(reservedSlot.start, currentTime);
            }

            // 空闲时段长度 = reservedSlot.start - lastEnd
            if (reservedSlot.start > lastEnd) {
                if (reservedSlot.start - lastEnd >= MIN_TIME_THRESHOLD) {
                    hasAvailableTime = true;
                    break;
                }
                // 空闲时段 < 5 分钟（边界值），跳过该小空闲，继续占用 lastEnd
                lastEnd = reservedSlot.start;
            }

            const reservedStart = Math.max(reservedSlot.start, currentTime >= 0 ? currentTime : 0);
            if (reservedStart < reservedSlot.end) {
                lastEnd = reservedSlot.end;
            }
        }

        if (lastEnd < 24) {
            if (currentTime >= 0 && lastEnd < currentTime) {
                lastEnd = currentTime;
            }
            // 末段空闲时段（lastEnd 到 24）必须 >= 5 分钟才算可预约
            if (24 - lastEnd >= MIN_TIME_THRESHOLD) {
                hasAvailableTime = true;
            }
        }

        // 如果有可预约时间，返回partial（部分可预约），否则返回unavailable（无空闲时段，不可预约）
        return hasAvailableTime ? 'partial' : 'unavailable';
    };

    

    // 过滤车辆列表
    const filteredVehicles = allVehicles().filter(vehicle => {
        // 按车牌号搜索过滤
        const matchesSearch = vehicle && vehicle.plateNumber &&
            vehicle.plateNumber.toLowerCase().includes(searchTerm.toLowerCase());

        // 按分组过滤
        // 转换车辆的group ID为分组名称，然后与selectedGroup比较
        const vehicleGroupName = typeof vehicle.group === 'number' ? getGroupNameById(vehicle.group) : vehicle.group;
        const matchesGroup = !selectedGroup || vehicleGroupName === selectedGroup;

        // 按座位数过滤
        let matchesSeats = true;
        if (selectedSeats !== '全部' && selectedSeats !== '座位数') {
            // 从座位名称中提取数字部分
            const selectedSeatsValue = parseInt(selectedSeats.replace(/[^0-9]/g, ''));

            // 检查车辆座位数是否匹配
            matchesSeats = vehicle && vehicle.seats === selectedSeatsValue;
        }

        // 按城市归属地过滤
        const matchesCity = !selectedCity || vehicle.cityName === selectedCity;

        // 按状态过滤（顶部"可预约/已预约/不可预约"徽章）
        let matchesStatus = true;
        if (statusFilter !== 'all') {
            const status = getVehicleStatus(vehicle);
            matchesStatus = status === statusFilter;
        }

        return matchesSearch && matchesGroup && matchesSeats && matchesCity && matchesStatus;
    });
    
    // 生成未来7天的日期
    const next7Days = generateNext7Days();

// 计算每种状态的车牌数量
    const statusCounts = (() => {
        const counts = { available: 0, partial: 0, reserved: 0, unavailable: 0 };
        const list = allVehicles();
        list.forEach(vehicle => {
            const status = getVehicleStatus(vehicle);
            if (counts[status] !== undefined) {
                counts[status] += 1;
            }
        });
        return counts;
    })();

    // 状态徽章点击切换筛选
    const handleStatusFilter = (status) => {
        setStatusFilter(prev => (prev === status ? 'all' : status));
    };



    return (
        <div className="vehicle-select-page">
            <div className="vehicle-select-header">
                <h2>车辆预约信息</h2>
                <div className="header-right"></div>
            </div>
            
            {/* 日期选择器 */}
            <div className="date-selector">
                {next7Days.map(day => (
                    <div 
                        key={day.date}
                        className={`date-item ${selectedDate === day.date ? 'selected' : ''}`}
                        onClick={() => handleDateSelect(day.date)}
                    >
                        <div className="date-day">{day.day}</div>
                        <div className="date-weekday">{day.weekDay}</div>
                    </div>
                ))}
            </div>
            
            {/* 搜索框 */}
            <div className="search-container">
                <input 
                    type="text" 
                    placeholder="输入车牌号"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
            </div>
            
            {/* 筛选条件 */}
            <div className="filter-container" ref={filterContainerRef}>
                {/* 分组筛选 */}
                <div ref={groupFilterRef} className={`filter-item ${isGroupDropdownOpen ? 'active' : ''}`} onClick={() => {
                    setIsGroupDropdownOpen(!isGroupDropdownOpen);
                    setIsSeatsDropdownOpen(false);
                }}>
                    <span className="filter-arrow">{selectedGroup || '使用场景'} ▼</span>
                    {isGroupDropdownOpen && (
                        <DropdownPortal isOpen={isGroupDropdownOpen} filterRef={groupFilterRef}>
                            <div className="filter-dropdown">
                                <div 
                                    className="filter-option"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleGroupSelect('');
                                        setIsGroupDropdownOpen(false);
                                    }}
                                >
                                    全部
                                </div>
                                {vehicleGroups.map(group => (
                                    <div 
                                        key={group.groupId} 
                                        className="filter-option"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleGroupSelect(group.groupName);
                                            setIsGroupDropdownOpen(false);
                                        }}
                                    >
                                        {group.groupName}
                                    </div>
                                ))}
                            </div>
                        </DropdownPortal>
                    )}
                </div>
                
                {/* 座位数筛选 */}
                <div ref={seatsFilterRef} className={`filter-item ${isSeatsDropdownOpen ? 'active' : ''}`} onClick={() => {
                    setIsSeatsDropdownOpen(!isSeatsDropdownOpen);
                    setIsGroupDropdownOpen(false);
                    setIsCityDropdownOpen(false);
                }}>
                    <span className="filter-arrow">{selectedSeats === '全部' ? '座位数' : selectedSeats} ▼</span>
                    {isSeatsDropdownOpen && (
                        <DropdownPortal isOpen={isSeatsDropdownOpen} filterRef={seatsFilterRef}>
                            <div className="filter-dropdown">
                                {availableSeats.map((seat, index) => (
                                    <div
                                        key={index}
                                        className="filter-option"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSeatsSelect(seat.seatName);
                                            setIsSeatsDropdownOpen(false);
                                        }}
                                    >
                                        {seat.seatName}
                                    </div>
                                ))}
                            </div>
                        </DropdownPortal>
                    )}
                </div>

                {/* 城市归属地筛选 */}
                <div ref={cityFilterRef} className={`filter-item ${isCityDropdownOpen ? 'active' : ''}`} onClick={() => {
                    setIsCityDropdownOpen(!isCityDropdownOpen);
                    setIsGroupDropdownOpen(false);
                    setIsSeatsDropdownOpen(false);
                }}>
                    <span className="filter-arrow">{selectedCity || '车辆归属地'} ▼</span>
                    {isCityDropdownOpen && (
                        <DropdownPortal isOpen={isCityDropdownOpen} filterRef={cityFilterRef}>
                            <div className="filter-dropdown">
                                <div
                                    className="filter-option"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedCity('');
                                        setIsCityDropdownOpen(false);
                                    }}
                                >
                                    全部
                                </div>
                                {cityList.map(city => (
                                    <div
                                        key={city}
                                        className="filter-option"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedCity(city);
                                            setIsCityDropdownOpen(false);
                                        }}
                                    >
                                        {city}
                                    </div>
                                ))}
                            </div>
                        </DropdownPortal>
                    )}
                </div>
            </div>
            
            {/* 状态说明 - 可点击筛选 */}
            <div className="status-legend">
                <div
                    className={`status-item clickable ${statusFilter === 'available' ? 'active' : ''}`}
                    onClick={() => handleStatusFilter('available')}
                >
                    <span className="status-dot available"></span>
                    <span>可预约</span>
                    <span className="status-count">{statusCounts.available}</span>
                </div>
                <div
                    className={`status-item clickable ${statusFilter === 'partial' ? 'active' : ''}`}
                    onClick={() => handleStatusFilter('partial')}
                >
                    <span className="status-dot reserved"></span>
                    <span>已预约</span>
                    <span className="status-count">{statusCounts.partial}</span>
                </div>
                <div
                    className={`status-item clickable ${statusFilter === 'unavailable' ? 'active' : ''}`}
                    onClick={() => handleStatusFilter('unavailable')}
                >
                    <span className="status-dot unavailable"></span>
                    <span>不可预约</span>
                    <span className="status-count">{statusCounts.unavailable}</span>
                </div>
            </div>
            
            {/* 车辆列表 */}
            <div className="vehicles-list">
                {loading ? (
                    <div className="loading-indicator">
                        <p>正在加载车辆数据...</p>
                    </div>
                ) : filteredVehicles.length === 0 ? (
                    <div className="no-vehicles">
                        <p>暂无车辆数据</p>
                    </div>
                ) : (
                    filteredVehicles.map(vehicle => {
                        const status = getVehicleStatus(vehicle);
                        
                        // 检查车辆ID是否匹配，并过滤重复数据
                const vehicleTimes = vehicleUsageTimes
                    .filter(item => {
                        const isVehicleMatch = item.vehicleId && vehicle.vehicleId && 
                            item.vehicleId === vehicle.vehicleId;
                        
                        // 检查选定日期是否在预约时间范围内（包括开始日期、中间日期和结束日期）
                        const startDateTime = new Date(item.startTime);
                        const endDateTime = new Date(item.endTime);
                        const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                        const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
                        
                        const dateParts = selectedDate.split('-');
                        const selectedDateOnly = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                        
                        const isDateInRange = selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
                        
                        return isVehicleMatch && isDateInRange;
                    })
                    // 去重：根据startTime和endTime判断是否为重复数据
                    .filter((item, index, self) => {
                        const key = `${item.startTime}-${item.endTime}`;
                        return self.findIndex(t => `${t.startTime}-${t.endTime}` === key) === index;
                    });
                        
                        return (
                            <div 
                            key={vehicle.plateNumber} 
                            className="vehicle-wrapper"
                            >
                            {/* 车辆信息行：图片 + 信息 + 状态 */}
                            <div 
                                className="vehicle-item"
                                onClick={() => handleVehicleSelect(vehicle)}
                            >
                                {/* 左侧车辆图片 */}
                                <div className="vehicle-image">
                                    {vehicle.imageUrl ? (
                                        <img
                                            src={vehicle.imageUrl}
                                            alt={vehicle.plateNumber}
                                            className="vehicle-image-img"
                                        />
                                    ) : (
                                        <div className="default-vehicle-image">🚗</div>
                                    )}
                                </div>

                                {/* 右侧车辆信息 */}
                                <div className="vehicle-right-info">
                                    {/* 第一行：车牌号 */}
                                    <div className="vehicle-info-line">
                                        <div className="vehicle-plate-number">{vehicle.plateNumber}</div>
                                    </div>
                                    {/* 第二行：品牌型号 + 座位数 */}
                                    <div className="vehicle-info-line">
                                        <div className="vehicle-type">{vehicle.modelName || vehicle.vehicleType || '未知车型'}</div>
                                        <div className="vehicle-seats">{vehicle.seats || 5}座</div>

                                    </div>
                                    {/* 第三行：使用场景 */}
                                    {(() => {
                                        const vehicleGroupName = typeof vehicle.group === 'number' ? getGroupNameById(vehicle.group) : vehicle.group;
                                        return vehicleGroupName && (
                                            <div className="vehicle-info-line">
                                                <div className="vehicle-group">{vehicleGroupName}</div>
                                            </div>
                                        );
                                    })()}
                                </div>
                                {/* 中间归属地（大字突出显示） */}
                                {vehicle.cityName && (
                                    <div className="vehicle-city-center">
                                        {vehicle.cityName}
                                    </div>
                                )}
                                {/* 状态标签 */}
                                <div className="vehicle-status-container">
                                    <div className={`vehicle-status-indicator ${status}`}></div>
                                </div>
                            </div>

                                {/* 24小时时间轴 - 单独在下方 */}
                                <div className="time-axis-container">
                                    {/* 第一段时间轴 0-12 */}
                                    {renderTimeAxisSegment(vehicle, vehicleTimes, selectedDate, 0, 12, status, formatTime, hoveredReservationId, popupPosition, setPopupPosition, setHoveredReservationId, setHoveredReservationData, setShowReservationDetail)}

                                    {/* 第二段时间轴 12-24 */}
                                    {renderTimeAxisSegment(vehicle, vehicleTimes, selectedDate, 12, 24, status, formatTime, hoveredReservationId, popupPosition, setPopupPosition, setHoveredReservationId, setHoveredReservationData, setShowReservationDetail)}

                                    {/* 时间状态摘要 */}
                                    {(() => {
                                        const dateParts = selectedDate.split('-');
                                        const selectedDateOnly = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                                        const now = new Date();
                                        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

                                        let currentTime = -1;
                                        if (selectedDateOnly.getTime() === currentDate.getTime()) {
                                            currentTime = now.getHours() + (now.getMinutes() / 60);
                                        }
                                        // ... existing code ...
                                        const vehicleTimesForSummary = vehicleUsageTimes
                                            .filter(item => {
                                                const isVehicleMatch = item.vehicleId && vehicle.vehicleId && item.vehicleId === vehicle.vehicleId;
                                                const startDateTime = new Date(item.startTime);
                                                const endDateTime = new Date(item.endTime);
                                                const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                                                const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
                                                const isDateInRange = selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
                                                return isVehicleMatch && isDateInRange;
                                            })
                                            .filter((item, index, self) => {
                                                const key = `${item.startTime}-${item.endTime}`;
                                                return self.findIndex(t => `${t.startTime}-${t.endTime}` === key) === index;
                                            });

                                        // 计算可预约时间段
                                        const mergedSlots = [];
                                        const sortedSlots = [...vehicleTimesForSummary].sort((a, b) => {
                                            const aStart = new Date(a.startTime);
                                            const bStart = new Date(b.startTime);
                                            return aStart - bStart;
                                        });

                                        for (const time of sortedSlots) {
                                            const startDateTime = new Date(time.startTime);
                                            const endDateTime = new Date(time.endTime);
                                            const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                                            const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

                                            let startHour, endHour;
                                            if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
                                                startHour = startDateTime.getHours() + startDateTime.getMinutes() / 60;
                                                endHour = endDateTime.getHours() + endDateTime.getMinutes() / 60;
                                            } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
                                                startHour = startDateTime.getHours() + startDateTime.getMinutes() / 60;
                                                endHour = 24;
                                            } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
                                                startHour = 0;
                                                endHour = endDateTime.getHours() + endDateTime.getMinutes() / 60;
                                            } else {
                                                startHour = 0;
                                                endHour = 24;
                                            }

                                            if (mergedSlots.length === 0) {
                                                mergedSlots.push({ start: startHour, end: endHour });
                                            } else {
                                                const lastSlot = mergedSlots[mergedSlots.length - 1];
                                                if (startHour <= lastSlot.end) {
                                                    lastSlot.end = Math.max(lastSlot.end, endHour);
                                                } else {
                                                    mergedSlots.push({ start: startHour, end: endHour });
                                                }
                                            }
                                        }

                                        // 生成最终的时间段
                                        const finalSlots = [];
                                        let lastEnd = 0;

                                        for (const reservedSlot of mergedSlots) {
                                            if (currentTime >= 0 && lastEnd < currentTime) {
                                                const unavailableEnd = Math.min(reservedSlot.start, currentTime);
                                                if (unavailableEnd > lastEnd) {
                                                    finalSlots.push({ start: lastEnd, end: unavailableEnd, status: 'unavailable' });
                                                }
                                                lastEnd = unavailableEnd;
                                            }

                                            if (reservedSlot.start > lastEnd) {
                                                finalSlots.push({ start: lastEnd, end: reservedSlot.start, status: 'available' });
                                            }

                                            const reservedStart = Math.max(reservedSlot.start, currentTime >= 0 ? currentTime : 0);
                                            if (reservedStart < reservedSlot.end) {
                                                finalSlots.push({ start: reservedStart, end: reservedSlot.end, status: 'reserved' });
                                            }

                                            lastEnd = reservedSlot.end;
                                        }

                                        if (lastEnd < 24) {
                                            if (currentTime >= 0 && lastEnd < currentTime) {
                                                finalSlots.push({ start: lastEnd, end: currentTime, status: 'unavailable' });
                                                lastEnd = currentTime;
                                            }
                                            if (lastEnd < 24) {
                                                finalSlots.push({ start: lastEnd, end: 24, status: 'available' });
                                            }
                                        }

                                        if (mergedSlots.length === 0) {
                                            if (currentTime >= 0) {
                                                finalSlots.push({ start: 0, end: currentTime, status: 'unavailable' });
                                                finalSlots.push({ start: currentTime, end: 24, status: 'available' });
                                            } else {
                                                finalSlots.push({ start: 0, end: 24, status: 'available' });
                                            }
                                        }

                                        // ... existing code ...
                                        // 格式化时间段显示
                                        const formatHour = (hour) => {
                                            const h = Math.floor(hour);
                                            const m = Math.round((hour - h) * 60);
                                            return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                                        };

                                        // 最小时间间隔阈值（单位：小时），小于这个值的时间段不显示
                                        const MIN_TIME_THRESHOLD = 5 / 60; // 5分钟

                                        // 对finalSlots进行去重，避免相同时间段重复显示
                                        const uniqueSlots = [];
                                        const seenTimeRanges = new Set();

                                        finalSlots.forEach(slot => {
                                            // 使用起始时间和结束时间作为唯一标识
                                            const timeKey = `${slot.start}-${slot.end}`;
                                            if (!seenTimeRanges.has(timeKey)) {
                                                seenTimeRanges.add(timeKey);
                                                uniqueSlots.push(slot);
                                            }
                                        });

                                        // 过滤掉时间间隔过短的可预约时间段
                                        const availableSlots = uniqueSlots
                                            .filter(s => s.status === 'available')
                                            .filter(s => (s.end - s.start) >= MIN_TIME_THRESHOLD);

                                        if (availableSlots.length === 0) {
                                            return <div className="time-axis-summary">🚫 今日无空闲时段</div>;
                                        }

                                        if (availableSlots.length === 1) {
                                            return (
                                                <div className="time-axis-summary">
                                                    ✅ 可预约：{formatHour(availableSlots[0].start)}-{formatHour(availableSlots[0].end)}
                                                </div>
                                            );
                                        }

                                        return (
                                            <div className="time-axis-summary">
                                                ✅ 可预约：{availableSlots.map((slot, idx) => (
                                                <span key={idx}>
                                                    {formatHour(slot.start)}-{formatHour(slot.end)}
                                                    {idx < availableSlots.length - 1 && '、'}
                                                </span>
                                            ))}
                                            </div>
                                        );

                                    })()}


                                    

                                    
                            </div>
                        </div>
                        );
                    })
                )}
            </div>
            
            {/* 底部返回按钮 */}
            <div className="bottom-actions">
                <button className="back-to-reserve-btn" onClick={() => navigate('/')}>
                    返回首页
                </button>
            </div>

            {/* 预约详情弹窗 */}
            {showReservationDetail && (hoveredReservationData || selectedReservation) && (
                <div
                    className="reservation-detail-modal"
                    style={{
                        top: popupPosition.top,
                        left: popupPosition.left
                    }}
                    onMouseLeave={() => {
                        setHoveredReservationId(null);
                        setHoveredReservationData(null);
                        setShowReservationDetail(false);
                    }}
                >
                    <div className="reservation-detail-content">
                        <div className="reservation-detail-body">
                            <div className="reservation-detail-item">
                                <span className="label">预约时间：</span>
                                <span>{formatTime((hoveredReservationData || selectedReservation).startTime)} - {formatTime((hoveredReservationData || selectedReservation).endTime)}</span>
                            </div>
                            {(hoveredReservationData || selectedReservation).applicantName && (
                                <div className="reservation-detail-item">
                                    <span className="label">申请人：</span>
                                    <span>{(hoveredReservationData || selectedReservation).applicantName}</span>
                                </div>
                            )}
                            {(hoveredReservationData || selectedReservation).applicantDept && (
                                <div className="reservation-detail-item">
                                    <span className="label">部门：</span>
                                    <span>{(hoveredReservationData || selectedReservation).applicantDept}</span>
                                </div>
                            )}
                            {(hoveredReservationData || selectedReservation).applicantPhone && (
                                <div className="reservation-detail-item">
                                    <span className="label">联系电话：</span>
                                    <span>{(hoveredReservationData || selectedReservation).applicantPhone}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}